import { useState, useMemo } from "react";
import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Grid,
  Image,
  TextInput,
  Select,
  SimpleGrid,
  Anchor,
  Divider,
  Box,
  Center,
  Loader,
  Button,
  Modal,
  Alert,
} from "@mantine/core";
import {
  IconSearch,
  IconUsers,
  IconClock,
  IconTrophy,
  IconExternalLink,
  IconDice,
  IconRefresh,
  IconInfoCircle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import bggCollection from "../data/bgg_collection.json";

interface Game {
  id: number;
  name: string;
  year: number | null;
  min_players: number;
  max_players: number;
  min_playtime: number;
  max_playtime: number;
  weight: number;
  avg_rating: number;
  bayes_avg: number;
  overall_rank: number | null;
  numplays: number;
  image: string;
  categories: string[];
  mechanics: string[];
  designers: string[];
  publishers: string[];
  bgg_url: string;
}

export function TabletopGamingPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [filterCategory, setFilterCategory] = useState("");
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const games: Game[] = bggCollection.games || [];

  // Sync BGG collection
  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch("http://localhost:4000/bgg/sync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: "Daddieo" }),
      });

      const result = await response.json();

      if (result.success) {
        notifications.show({
          title: "Sync Complete!",
          message:
            "Your BGG collection has been updated. Refresh the page to see changes.",
          color: "green",
          icon: <IconRefresh size={16} />,
        });
        setSyncModalOpen(false);
        // Optionally reload the page to show new data
        setTimeout(() => window.location.reload(), 2000);
      } else {
        notifications.show({
          title: "Sync Failed",
          message: result.error || "Failed to sync BGG collection",
          color: "red",
        });
      }
    } catch (error) {
      notifications.show({
        title: "Sync Error",
        message: "Failed to connect to sync service",
        color: "red",
      });
    } finally {
      setSyncing(false);
    }
  };

  // Get unique categories for filter
  const allCategories = useMemo(() => {
    const categories = new Set<string>();
    games.forEach((game) => {
      game.categories.forEach((cat) => categories.add(cat));
    });
    return Array.from(categories).sort();
  }, [games]);

  // Filter and sort games
  const filteredGames = useMemo(() => {
    let filtered = games.filter((game) => {
      const matchesSearch =
        game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        game.designers.some((d) =>
          d.toLowerCase().includes(searchTerm.toLowerCase())
        );
      const matchesCategory =
        !filterCategory || game.categories.includes(filterCategory);
      return matchesSearch && matchesCategory;
    });

    // Sort games
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "rating":
          return b.avg_rating - a.avg_rating;
        case "year":
          return (b.year || 0) - (a.year || 0);
        case "plays":
          return b.numplays - a.numplays;
        case "weight":
          return b.weight - a.weight;
        case "name":
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [games, searchTerm, sortBy, filterCategory]);

  const getWeightLabel = (weight: number) => {
    if (weight < 2) return "Light";
    if (weight < 3) return "Medium-Light";
    if (weight < 4) return "Medium";
    if (weight < 4.5) return "Medium-Heavy";
    return "Heavy";
  };

  const getWeightColor = (weight: number) => {
    if (weight < 2) return "green";
    if (weight < 3) return "lime";
    if (weight < 4) return "yellow";
    if (weight < 4.5) return "orange";
    return "red";
  };

  const getPlayersText = (game: Game) => {
    if (game.min_players === game.max_players) {
      return `${game.min_players}`;
    }
    return `${game.min_players}-${game.max_players}`;
  };

  const getPlaytimeText = (game: Game) => {
    if (game.min_playtime === game.max_playtime) {
      return `${game.min_playtime}min`;
    }
    return `${game.min_playtime}-${game.max_playtime}min`;
  };

  if (games.length === 0) {
    return (
      <Stack gap="xl">
        <Card
          p="xl"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <Stack gap="md" align="center">
            <IconDice size={48} color="white" />
            <Title order={1} c="white" ta="center">
              Tabletop Gaming and Conflict Simulation
            </Title>
            <Text c="white" ta="center" size="lg">
              My BoardGameGeek collection will appear here once loaded
            </Text>
          </Stack>
        </Card>

        <Card
          withBorder
          style={{
            backgroundColor: "rgba(18, 18, 26, 0.8)",
            borderColor: "rgba(139, 92, 246, 0.4)",
            /* backdropFilter: 'blur(disabled)' - DISABLED */
          }}
        >
          <Stack gap="md" align="center">
            <Loader size="lg" />
            <Text style={{ color: "#d1d5db" }}>
              Collection not yet loaded. Run the BGG fetcher script to populate
              this page.
            </Text>
            <Text size="sm" style={{ color: "#a1a1aa" }} ta="center">
              Use:{" "}
              <code>
                python scripts/bgg_collection_fetcher.py --user
                YOUR_BGG_USERNAME
              </code>
            </Text>
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="xl">
      {/* Header */}
      <Card
        p="xl"
        withBorder
        style={{
          backgroundColor: "rgba(18, 18, 26, 0.8)",
          borderColor: "rgba(139, 92, 246, 0.4)",
        }}
      >
        <Stack gap="md" align="center">
          <IconDice size={48} color="#8b5cf6" />
          <Title order={1} c="white" ta="center">
            Tabletop Gaming and Conflict Simulation
          </Title>
          <Text c="gray.2" ta="center" size="sm">
            Last updated: {bggCollection.updated}
          </Text>
        </Stack>
      </Card>

      {/* Total Games and Filters */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <Card
          withBorder
          style={{
            backgroundColor: "rgba(18, 18, 26, 0.8)",
            borderColor: "rgba(139, 92, 246, 0.4)",
            /* backdropFilter: 'blur(disabled)' - DISABLED */
          }}
        >
          <Stack gap="xs" align="center">
            <IconTrophy size={24} color="var(--mantine-color-blue-6)" />
            <Text fw={600} size="lg" style={{ color: "#ffffff" }}>
              {games.length}
            </Text>
            <Text size="sm" style={{ color: "#a1a1aa" }}>
              Total Games
            </Text>
          </Stack>
        </Card>
        <Card
          withBorder
          style={{
            gridColumn: "span 2",
            backgroundColor: "rgba(18, 18, 26, 0.8)",
            borderColor: "rgba(139, 92, 246, 0.4)",
            /* backdropFilter: 'blur(disabled)' - DISABLED */
          }}
        >
          <Stack gap="md">
            <Title order={3} style={{ color: "#8b5cf6" }}>
              Filter & Sort
            </Title>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <TextInput
                placeholder="Search games or designers..."
                leftSection={<IconSearch size={16} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
              />
              <Select
                placeholder="Filter by category"
                data={[
                  { value: "", label: "All Categories" },
                  ...allCategories.map((cat) => ({ value: cat, label: cat })),
                ]}
                value={filterCategory}
                onChange={(value) => setFilterCategory(value || "")}
                clearable
              />
              <Select
                placeholder="Sort by..."
                data={[
                  { value: "name", label: "Name" },
                  { value: "rating", label: "Rating" },
                  { value: "year", label: "Year" },
                  { value: "plays", label: "Play Count" },
                  { value: "weight", label: "Complexity" },
                ]}
                value={sortBy}
                onChange={(value) => setSortBy(value || "name")}
              />
            </SimpleGrid>
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Games Grid */}
      <Grid>
        {filteredGames.map((game) => (
          <Grid.Col key={game.id} span={{ base: 12, sm: 6, lg: 4 }}>
            <Card
              h="100%"
              p="md"
              withBorder
              style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "rgba(18, 18, 26, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="sm" style={{ flex: 1 }}>
                {/* Game Image */}
                {game.image && (
                  <Center>
                    <Image
                      src={game.image}
                      alt={game.name}
                      h={140}
                      w="auto"
                      fit="contain"
                      fallbackSrc="https://via.placeholder.com/140x140?text=No+Image"
                    />
                  </Center>
                )}

                <Divider />

                {/* Game Info */}
                <Stack gap="xs" style={{ flex: 1 }}>
                  <Group justify="space-between" align="flex-start">
                    <Box style={{ flex: 1 }}>
                      <Anchor
                        href={game.bgg_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        td="none"
                      >
                        <Title
                          order={4}
                          lineClamp={2}
                          size="md"
                          style={{ color: "#ffffff" }}
                        >
                          {game.name}
                        </Title>
                      </Anchor>
                      {game.year && (
                        <Text size="sm" style={{ color: "#a1a1aa" }}>
                          ({game.year})
                        </Text>
                      )}
                    </Box>
                    <Anchor
                      href={game.bgg_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconExternalLink size={16} />
                    </Anchor>
                  </Group>

                  {/* Designers */}
                  {game.designers.length > 0 && (
                    <Text size="sm" style={{ color: "#a1a1aa" }} lineClamp={1}>
                      by {game.designers.join(", ")}
                    </Text>
                  )}

                  <Divider size="xs" />

                  {/* Game Stats */}
                  <Group gap="xs" wrap="wrap">
                    {game.min_players > 0 && (
                      <Badge size="sm" variant="filled" color="blue">
                        <Group gap={4}>
                          <IconUsers size={12} />
                          {getPlayersText(game)}
                        </Group>
                      </Badge>
                    )}
                    {game.min_playtime > 0 && (
                      <Badge size="sm" variant="filled" color="green">
                        <Group gap={4}>
                          <IconClock size={12} />
                          {getPlaytimeText(game)}
                        </Group>
                      </Badge>
                    )}
                    {game.weight > 0 && (
                      <Badge
                        size="sm"
                        variant="filled"
                        color={getWeightColor(game.weight)}
                      >
                        {getWeightLabel(game.weight)} ({game.weight.toFixed(1)})
                      </Badge>
                    )}
                  </Group>

                  {/* Rating and Plays */}
                  <Group justify="space-between" align="center">
                    <Group gap="xs">
                      {game.avg_rating > 0 && (
                        <Badge size="sm" variant="filled" color="yellow">
                          ⭐ {game.avg_rating.toFixed(1)}
                        </Badge>
                      )}
                      {game.overall_rank && (
                        <Badge size="sm" variant="filled" color="gray">
                          #{game.overall_rank}
                        </Badge>
                      )}
                    </Group>
                    {game.numplays > 0 && (
                      <Text size="sm" style={{ color: "#a1a1aa" }} fw={500}>
                        {game.numplays} play{game.numplays !== 1 ? "s" : ""}
                      </Text>
                    )}
                  </Group>

                  <Divider size="xs" />

                  {/* Categories */}
                  {game.categories.length > 0 && (
                    <Group gap={4}>
                      {game.categories.slice(0, 3).map((category) => (
                        <Badge
                          key={category}
                          size="xs"
                          variant="filled"
                          color="gray"
                        >
                          {category}
                        </Badge>
                      ))}
                      {game.categories.length > 3 && (
                        <Text size="xs" style={{ color: "#a1a1aa" }} fw={500}>
                          +{game.categories.length - 3} more
                        </Text>
                      )}
                    </Group>
                  )}
                </Stack>
              </Stack>
            </Card>
          </Grid.Col>
        ))}
      </Grid>

      {filteredGames.length === 0 && (
        <Card
          withBorder
          style={{
            backgroundColor: "rgba(18, 18, 26, 0.8)",
            borderColor: "rgba(139, 92, 246, 0.4)",
            /* backdropFilter: 'blur(disabled)' - DISABLED */
          }}
        >
          <Center py="xl">
            <Stack align="center" gap="md">
              <IconSearch size={48} color="var(--mantine-color-gray-5)" />
              <Text style={{ color: "#d1d5db" }}>
                No games found matching your criteria
              </Text>
            </Stack>
          </Center>
        </Card>
      )}

      {/* Sync Modal */}
      <Modal
        opened={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        title="Sync BGG Collection"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconInfoCircle size={16} />} color="blue">
            This will fetch your latest BoardGameGeek collection data and update
            the page. The sync process may take a few minutes depending on your
            collection size.
          </Alert>

          <Text size="sm" c="dimmed">
            Current collection:{" "}
            <strong>{bggCollection.total_games} games</strong>
          </Text>
          <Text size="sm" c="dimmed">
            Last updated: <strong>{bggCollection.updated}</strong>
          </Text>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setSyncModalOpen(false)}
              disabled={syncing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSync}
              loading={syncing}
              leftSection={!syncing ? <IconRefresh size={16} /> : undefined}
            >
              {syncing ? "Syncing..." : "Start Sync"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
